<template>
  <div ref="root" class="lbl-dropdown" @keydown="onKeydown">
    <LgButton size="sm" variant="ghost" :aria-pressed="open" @click="toggleOpen">
      <template #icon>
        <component
          :is="mode === 'split' ? SplitSquareHorizontal : Filter"
          :size="14"
          aria-hidden="true"
        />
      </template>
      {{ mode === 'split' ? 'Split by' : 'Filter' }}
    </LgButton>

    <div
      v-if="open"
      class="panel"
      role="dialog"
      :aria-label="mode === 'split' ? 'Split by a label' : 'Filter by a label'"
    >
      <template v-if="!pickedKey">
        <div v-if="!labelOptions.length" class="panel-note">
          This metric has no labels to {{ mode === 'split' ? 'split' : 'filter' }} by.
        </div>
        <ul v-else class="key-list" role="listbox" :aria-label="'Labels'">
          <li v-for="k in labelOptions" :key="k">
            <button type="button" class="key-item" @click="pickKey(k)">
              <span class="key-name">{{ labelDisplayName(k) }}</span>
            </button>
          </li>
        </ul>
      </template>

      <template v-else>
        <button type="button" class="back-btn" @click="goBack">
          <ChevronLeft :size="14" aria-hidden="true" />
          {{ labelDisplayName(pickedKey) }}
        </button>

        <!-- Split mode commits immediately on key pick; filter mode needs a value. -->
        <div class="mode-row" role="group" aria-label="Include or exclude">
          <button
            type="button"
            class="mode-btn"
            :class="{ on: !negate }"
            :aria-pressed="!negate"
            @click="negate = false"
          >
            is =
          </button>
          <button
            type="button"
            class="mode-btn"
            :class="{ on: negate }"
            :aria-pressed="negate"
            @click="negate = true"
          >
            is not ≠
          </button>
        </div>

        <LgInput
          v-model="valueText"
          class="value-input"
          placeholder="label value…"
          aria-label="Label value"
          @keydown.enter.prevent="commit"
        />

        <div v-if="valueLoading" class="panel-note">Loading values…</div>
        <ul v-else-if="values.length" class="suggest-list" role="listbox" aria-label="Label values">
          <li v-for="v in values" :key="v">
            <button type="button" class="suggest-item" @click="fill(v)">{{ v }}</button>
          </li>
        </ul>
        <div v-else-if="valueLoaded && !valueError" class="panel-note">
          No values found — type one.
        </div>
        <div v-else-if="valueError" class="panel-note err">Couldn't load values.</div>

        <LgButton
          size="sm"
          variant="primary"
          class="add-btn"
          :disabled="!valueText.trim()"
          @click="commit"
        >
          Add filter
        </LgButton>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeUnmount } from 'vue';
import { Filter, SplitSquareHorizontal, ChevronLeft } from 'lucide-vue-next';
import { api, type CatalogMetric, type MetricMatcher } from '../../api.js';
import { labelDisplayName } from '../../lib/metric-exploration.js';
import LgButton from '../../ui/LgButton.vue';
import LgInput from '../../ui/LgInput.vue';

const props = defineProps<{
  metric: CatalogMetric;
  /** Label keys offered (parent excludes `le` etc.). */
  labelOptions: string[];
  mode: 'split' | 'filter';
  /** Query window for value discovery. */
  from: number;
  to: number;
}>();

const emit = defineEmits<{ split: [label: string]; add: [matcher: MetricMatcher] }>();

const open = ref(false);
const pickedKey = ref<string | null>(null);
const valueText = ref('');
const negate = ref(false);

const values = ref<string[]>([]);
const valueLoading = ref(false);
const valueLoaded = ref(false);
const valueError = ref(false);
let seq = 0;

function toggleOpen() {
  open.value ? close() : openPanel();
}
function openPanel() {
  pickedKey.value = null;
  valueText.value = '';
  negate.value = false;
  resetValues();
  open.value = true;
}
function close() {
  open.value = false;
}
function goBack() {
  pickedKey.value = null;
  resetValues();
}
function resetValues() {
  seq++;
  values.value = [];
  valueLoading.value = false;
  valueLoaded.value = false;
  valueError.value = false;
}

function pickKey(k: string) {
  if (props.mode === 'split') {
    emit('split', k);
    close();
    return;
  }
  pickedKey.value = k;
  valueText.value = '';
  negate.value = false;
  resetValues();
  loadValues(k);
}

/** Discover distinct values for a label via an instant query grouped by it —
 * reuses the §2.4 contract, no new endpoint (task note: values from a query). */
function loadValues(label: string) {
  const s = ++seq;
  valueLoading.value = true;
  valueError.value = false;
  api.metrics
    .instant({
      source: props.metric.source || undefined,
      name: props.metric.name,
      target: props.metric.target || undefined,
      to: props.to,
      evalWindow: Math.max(15_000, props.to - props.from),
      agg: 'last',
      groupBy: [label],
    })
    .then((res) => {
      if (s !== seq) return;
      const set = new Set<string>();
      for (const r of res.results) {
        const v = r.labels[label];
        if (v !== undefined) set.add(v);
      }
      values.value = [...set].sort();
    })
    .catch(() => {
      if (s !== seq) return;
      valueError.value = true;
    })
    .finally(() => {
      if (s !== seq) return;
      valueLoading.value = false;
      valueLoaded.value = true;
    });
}

function fill(v: string) {
  valueText.value = v;
}

function commit() {
  if (!pickedKey.value) return;
  const value = valueText.value.trim();
  if (!value) return;
  emit('add', { label: pickedKey.value, op: negate.value ? 'neq' : 'eq', value });
  close();
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && open.value) {
    e.preventDefault();
    close();
  }
}

const root = ref<HTMLElement | null>(null);
function onDocPointer(e: MouseEvent) {
  if (open.value && root.value && !root.value.contains(e.target as Node)) close();
}
document.addEventListener('pointerdown', onDocPointer);
onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocPointer));
</script>

<style scoped>
.lbl-dropdown {
  position: relative;
  display: inline-flex;
}
.panel {
  position: absolute;
  z-index: var(--lg-z-menu);
  top: calc(100% + var(--lg-space-1));
  left: 0;
  width: 260px;
  max-width: calc(100vw - var(--lg-space-6));
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-2);
  padding: var(--lg-space-2);
  border-radius: var(--lg-radius-md);
  border: 1px solid var(--lg-border);
  background: var(--lg-bg-raised);
  box-shadow: var(--lg-shadow-2);
}
@media (max-width: 768px) {
  .panel {
    position: fixed;
    left: var(--lg-space-2);
    right: var(--lg-space-2);
    bottom: var(--lg-space-2);
    top: auto;
    width: auto;
    max-width: none;
    max-height: 75vh;
    overflow-y: auto;
  }
}
.panel-note {
  padding: var(--lg-space-2);
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-sm);
}
.panel-note.err {
  color: var(--lg-danger);
}
.key-list,
.suggest-list {
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 220px;
  overflow-y: auto;
}
.key-item {
  display: flex;
  width: 100%;
  padding: var(--lg-space-2);
  border: none;
  border-radius: var(--lg-radius-sm);
  background: transparent;
  color: var(--lg-text);
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.key-item:hover {
  background: var(--lg-bg-hover);
}
.key-name {
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-sm);
}
.suggest-item {
  display: block;
  width: 100%;
  padding: var(--lg-space-1) var(--lg-space-2);
  border: none;
  border-radius: var(--lg-radius-sm);
  background: transparent;
  color: var(--lg-text);
  font: inherit;
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-sm);
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.suggest-item:hover {
  background: var(--lg-bg-hover);
}
.mode-row {
  display: flex;
  gap: var(--lg-space-1);
}
.mode-btn {
  flex: 1;
  padding: var(--lg-space-1) var(--lg-space-2);
  border: 1px solid var(--lg-border-strong);
  border-radius: var(--lg-radius-sm);
  background: transparent;
  color: var(--lg-text-dim);
  font: inherit;
  font-size: var(--lg-fs-xs);
  cursor: pointer;
}
.mode-btn.on {
  border-color: var(--lg-accent);
  color: var(--lg-accent);
  background: var(--lg-bg-hover);
}
.back-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--lg-space-1);
  align-self: flex-start;
  border: none;
  background: transparent;
  color: var(--lg-text-dim);
  font: inherit;
  font-family: var(--lg-font-mono);
  padding: var(--lg-space-1);
  cursor: pointer;
}
.back-btn:hover {
  color: var(--lg-text);
}
.add-btn {
  align-self: flex-end;
}
</style>
